import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '@/shared/api/client'
import { Button, Field, Input, Select } from '@/shared/ui'
import { toast } from '@/shared/model/toast'
import {
  useBotGuard,
  useBots,
  useBotStore,
  useCreateBot,
  useCurrentBot,
  useDeleteBot,
  usePromoteBot,
  useRenameBot,
  useSelectBot,
} from '../model'
import type { Bot } from '@/shared/api/types'
import styles from './Bot.module.css'

interface BotSelectProps {
  companyId: number | undefined
  onNavigate?: () => void
}

/**
 * Переключатель ботов в сайдбаре.
 *
 * Заменяет прежний «селектор компании», который ничего не переключал и
 * которого клиенты принимали как раз за выбор бота.
 */
export function BotSelect({ companyId, onNavigate }: BotSelectProps) {
  useBotGuard(companyId)
  const bots = useBots()
  const current = useCurrentBot()
  const select = useSelectBot()
  const [open, setOpen] = useState(false)
  const [dialog, setDialog] = useState<'create' | 'manage' | null>(null)
  const rootRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    function onDown(event: MouseEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  function pick(botId: number | null) {
    setOpen(false)
    if (companyId === undefined) return
    void select(companyId, botId)
    onNavigate?.()
  }

  const items = bots.data?.items ?? []

  return (
    <div className={styles.root} ref={rootRef}>
      <button
        type="button"
        className={styles.trigger}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Выбрать бота"
        onClick={() => setOpen((v) => !v)}
      >
        <span className={styles.triggerLabel}>Бот</span>
        <span className={styles.triggerName}>{current.name}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true">
          <path d="M2.5 4L5 6.5L7.5 4" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {open && (
        <div className={styles.menu} role="menu">
          <button
            type="button"
            role="menuitemradio"
            aria-checked={current.isMain}
            className={`${styles.option} ${current.isMain ? styles.optionActive : ''}`}
            onClick={() => pick(null)}
          >
            <span className={styles.optionName}>Основной бот</span>
            {bots.data?.main.agentName && <span className={styles.optionMeta}>{bots.data.main.agentName}</span>}
          </button>
          {items.map((bot) => (
            <button
              key={bot.id}
              type="button"
              role="menuitemradio"
              aria-checked={current.botId === bot.id}
              className={`${styles.option} ${current.botId === bot.id ? styles.optionActive : ''}`}
              onClick={() => pick(bot.id)}
            >
              <span className={styles.optionName}>{bot.name}</span>
              <span className={styles.optionMeta}>
                {bot.agentName || 'без имени'}
                {bot.inUse > 0 ? ' · в обзвоне' : ''}
              </span>
            </button>
          ))}
          <div className={styles.menuSep} />
          <button
            type="button"
            role="menuitem"
            className={styles.menuAction}
            disabled={bots.data ? items.length >= bots.data.max : true}
            onClick={() => {
              setOpen(false)
              setDialog('create')
            }}
          >
            + Новый бот
          </button>
          {items.length > 0 && (
            <button
              type="button"
              role="menuitem"
              className={styles.menuAction}
              onClick={() => {
                setOpen(false)
                setDialog('manage')
              }}
            >
              Управлять ботами
            </button>
          )}
        </div>
      )}

      {dialog === 'create' && companyId !== undefined && (
        <CreateBotDialog
          items={items}
          onClose={() => setDialog(null)}
          onCreated={(bot) => {
            setDialog(null)
            void select(companyId, bot.id)
            toast.success(`Бот «${bot.name}» создан и выбран. Правьте его в «Настройке агента»`)
          }}
        />
      )}
      {dialog === 'manage' && <ManageBotsDialog items={items} onClose={() => setDialog(null)} />}
    </div>
  )
}

/** Модальное окно на родном dialog: фокус, Esc и затемнение даёт браузер. */
function Modal({
  labelledBy,
  busy,
  onClose,
  children,
}: {
  labelledBy: string
  busy: boolean
  onClose: () => void
  children: ReactNode
}) {
  const ref = useRef<HTMLDialogElement>(null)
  useEffect(() => {
    const dialog = ref.current
    if (dialog && !dialog.open) dialog.showModal()
    return () => {
      if (dialog?.open) dialog.close()
    }
  }, [])
  return (
    <dialog
      ref={ref}
      className={styles.dialog}
      aria-labelledby={labelledBy}
      onCancel={(event) => {
        event.preventDefault()
        if (!busy) onClose()
      }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      {children}
    </dialog>
  )
}

function CreateBotDialog({
  items,
  onClose,
  onCreated,
}: {
  items: Bot[]
  onClose: () => void
  onCreated: (bot: Bot) => void
}) {
  const create = useCreateBot()
  const currentId = useBotStore((s) => s.botId)
  const [name, setName] = useState('')
  const [copyFrom, setCopyFrom] = useState(currentId === null ? 'main' : String(currentId))
  const [error, setError] = useState('')

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!name.trim()) {
      setError('Назовите бота')
      return
    }
    try {
      const bot = await create.mutateAsync({
        name: name.trim(),
        copyFrom: copyFrom === 'main' ? null : Number(copyFrom),
      })
      onCreated(bot)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось создать бота')
    }
  }

  return (
    <Modal labelledBy="bot-create-title" busy={create.isPending} onClose={onClose}>
      <form className={styles.dialogForm} onSubmit={submit}>
        <h2 id="bot-create-title">Новый бот</h2>
        <p className={styles.dialogText}>
          Бот — копия анкеты, сценария и первых фраз. Правьте её как угодно: основной бот и идущие
          обзвоны не изменятся. База номеров, минуты, голос и часы работы — общие.
        </p>
        <Field label="Название" required error={error}>
          {(field) => (
            <Input
              {...field}
              autoFocus
              maxLength={60}
              value={name}
              invalid={Boolean(error)}
              placeholder="Например, Короткий скрипт"
              onChange={(event) => {
                setName(event.target.value)
                setError('')
              }}
            />
          )}
        </Field>
        <Field label="Скопировать настройки из">
          {(field) => (
            <Select
              {...field}
              value={copyFrom}
              options={[
                { value: 'main', label: 'Основной бот' },
                ...items.map((b) => ({ value: String(b.id), label: b.name })),
              ]}
              onChange={(event) => setCopyFrom(event.target.value)}
            />
          )}
        </Field>
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
          <Button type="submit" loading={create.isPending}>
            Создать
          </Button>
        </div>
      </form>
    </Modal>
  )
}

function ManageBotsDialog({ items, onClose }: { items: Bot[]; onClose: () => void }) {
  const rename = useRenameBot()
  const remove = useDeleteBot()
  const promote = usePromoteBot()
  const busy = rename.isPending || remove.isPending || promote.isPending
  const [editing, setEditing] = useState<number | null>(null)
  const [draft, setDraft] = useState('')
  const [error, setError] = useState('')

  async function saveName(bot: Bot) {
    if (!draft.trim()) {
      setError('Назовите бота')
      return
    }
    try {
      await rename.mutateAsync({ id: bot.id, name: draft.trim() })
      setEditing(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось переименовать')
    }
  }

  return (
    <Modal labelledBy="bot-manage-title" busy={busy} onClose={onClose}>
      <div className={styles.dialogForm}>
        <h2 id="bot-manage-title">Боты компании</h2>
        <ul className={styles.list}>
          {items.map((bot) => (
            <li key={bot.id} className={styles.row}>
              {editing === bot.id ? (
                <form
                  className={styles.renameForm}
                  onSubmit={(event) => {
                    event.preventDefault()
                    void saveName(bot)
                  }}
                >
                  <Input
                    autoFocus
                    aria-label="Новое название"
                    maxLength={60}
                    value={draft}
                    invalid={Boolean(error)}
                    onChange={(event) => {
                      setDraft(event.target.value)
                      setError('')
                    }}
                  />
                  <Button type="submit" size="sm" loading={rename.isPending}>
                    Сохранить
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                    Отмена
                  </Button>
                  {error && <span className={styles.rowError}>{error}</span>}
                </form>
              ) : (
                <>
                  <div className={styles.rowInfo}>
                    <span className={styles.optionName}>{bot.name}</span>
                    <span className={styles.optionMeta}>
                      {bot.agentName || 'без имени'} · этапов {bot.stages} · фраз {bot.openers}
                      {bot.inUse > 0 ? ` · в ${bot.inUse} обзвон(ах)` : ''}
                    </span>
                  </div>
                  <div className={styles.rowActions}>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy}
                      onClick={() => {
                        setEditing(bot.id)
                        setDraft(bot.name)
                        setError('')
                      }}
                    >
                      Переименовать
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      disabled={busy}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Сделать «${bot.name}» основным?\n\nОсновной бот получит его анкету, сценарий и фразы. Текущие настройки основного сохранятся отдельным ботом — их можно вернуть тем же способом.`,
                          )
                        )
                          promote.mutate(bot.id)
                      }}
                    >
                      Сделать основным
                    </Button>
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy || bot.inUse > 0}
                      title={bot.inUse > 0 ? 'Этим ботом идёт обзвон — сначала остановите запуски' : undefined}
                      onClick={() => {
                        if (window.confirm(`Удалить бота «${bot.name}»? Звонки, которые он сделал, останутся.`))
                          remove.mutate(bot.id)
                      }}
                    >
                      Удалить
                    </Button>
                  </div>
                </>
              )}
            </li>
          ))}
        </ul>
        <p className={styles.dialogText}>
          Удалить бота можно, когда им не идёт ни один обзвон: остановите запуски с этим ботом.
        </p>
        <div className={styles.dialogFooter}>
          <Button variant="secondary" onClick={onClose} disabled={busy}>
            Закрыть
          </Button>
        </div>
      </div>
    </Modal>
  )
}
